import React, { useState, useEffect, useMemo, Fragment, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Button,
  Chip,
  Stack,
  LinearProgress,
  ListItemSecondaryAction,
  CircularProgress,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Event as EventIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ArrowForward as ArrowForwardIcon,
  Restaurant as RestaurantIcon,
  Cake as CakeIcon,
  Assignment as AssignmentIcon,
  Notifications as NotificationsIcon,
  MonitorWeight as MonitorWeightIcon,
  LocalHospital as LocalHospitalIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  MedicalServices as MedicalServicesIcon,
  FitnessCenter as FitnessCenterIcon,
  Email as EmailIcon,
  Star as StarIcon,
  PriorityHigh as PriorityHighIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  Flag as FlagIcon,
  Attachment as AttachmentIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, FILE_BASE_URL } from '../config';
import { getFileUrl } from '../utils/urlHelper';
import DOMPurify from 'dompurify';

interface Paciente {
  id: string;
  nombre: string;
  fecha_nacimiento: string;
  genero: string;
  peso_kg: number;
  altura_cm: number;
  edad: number;
  email?: string;
  email2?: string;
  ruta_imagen_perfil?: string;
  fecha_modificacion?: string;
  fecha_creacion?: string;
  ultima_modificacion?: string;
  obra_social?: string;
  medico_id: string;
}

type TipoEvento = 'consulta' | 'plan alimentario' | 'recordatorio' | 'seguimiento' | 'cumpleaños';

interface Evento {
  id: string;
  titulo: string;
  fecha: string;
  tipo: TipoEvento;
  paciente_id: string;
  paciente_nombre: string;
  descripcion?: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [eventos, setEventos] = useState<Evento[]>([]);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  type TipoEventoConTodos = TipoEvento | 'todos';
  const [filtroEventos, setFiltroEventos] = useState<TipoEventoConTodos>('todos');
  const [eventosAMostrar, setEventosAMostrar] = useState(6); // Estado para controlar cuántos eventos mostrar
  
  // Estado para emails no leídos
  const [emailsNoLeidos, setEmailsNoLeidos] = useState<any[]>([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [emailsSkip, setEmailsSkip] = useState(0);
  const [emailsTotal, setEmailsTotal] = useState(0);
  const [loadingMoreEmails, setLoadingMoreEmails] = useState(false);
  const [hasMoreEmails, setHasMoreEmails] = useState(true);

  // Estado para modal de preview de email
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<any | null>(null);
  const [selectedEmailDetalle, setSelectedEmailDetalle] = useState<any | null>(null);
  const [loadingEmailDetalle, setLoadingEmailDetalle] = useState(false);

  // Generar eventos virtuales de cumpleaños - siempre se ejecuta
  const eventosCumpleanios = useMemo(() => {
    if (!pacientes || pacientes.length === 0) return [];
    const hoy = new Date();
    return pacientes
      .filter(p => p.fecha_nacimiento && /^\d{2}\/\d{2}\/\d{4}$/.test(p.fecha_nacimiento))
      .map(p => {
        const [dia, mes, anio] = p.fecha_nacimiento.split('/').map(Number);
        const fechaCumple = new Date(hoy.getFullYear(), mes - 1, dia);
        return {
          id: `cumple-${p.id}`,
          titulo: `Cumpleaños de ${p.nombre}`,
          fecha: fechaCumple.toLocaleDateString('en-CA'), // Usar formato local YYYY-MM-DD en lugar de UTC
          tipo: 'cumpleaños' as TipoEvento,
          paciente_id: p.id,
          paciente_nombre: p.nombre,
          descripcion: '',
        };
      });
  }, [pacientes]);

  // Unir eventos reales y virtuales - siempre se ejecuta
  const todosLosEventos = useMemo(() => {
    const idsCumple = new Set(eventosCumpleanios.map(e => e.id));
    const eventosSinCumpleReales = eventos.filter(e => !(e.tipo === 'cumpleaños' && idsCumple.has(e.id)));
    return [...eventosSinCumpleReales, ...eventosCumpleanios];
  }, [eventos, eventosCumpleanios]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Limpiar cualquier intervalo anterior
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }

        // Inicializar progreso
        setLoadingProgress(0);

        // Simular progreso de carga hasta 85% (más rápido)
        progressIntervalRef.current = setInterval(() => {
          setLoadingProgress(prev => {
            // Asegurar que nunca supere 85%
            if (prev >= 85) {
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
              return 85;
            }
            // Incremento más rápido
            const increment = Math.min(15 + Math.random() * 15, 85 - prev);
            return Math.min(prev + increment, 85);
          });
        }, 200); // Más rápido: cada 200ms

        // Cargar pacientes, eventos Y emails en paralelo
        const [pacientesRes, eventosRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/pacientes`),
          axios.get(`${API_BASE_URL}/eventos`)
        ]);
        
        setPacientes(pacientesRes.data);
        setEventos(eventosRes.data);
        
        // Cargar emails en paralelo después de obtener pacientes
        const emailsPromise = fetchEmailsNoLeidos(pacientesRes.data);
        
        // Esperar a que el progreso simulado llegue naturalmente a 85%
        // y que terminen todas las operaciones
        
        // Esperar a que termine la carga de emails
        await emailsPromise;
        
        // Detener el progreso simulado si aún está activo
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        
        // Completar el progreso solo cuando TODO esté cargado
        setLoadingProgress(100);
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Limpiar intervalo en caso de error
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setLoadingProgress(100);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 500); // Pausa reducida: 500ms en lugar de 1000ms
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  // Función para cargar emails no leídos
  const fetchEmailsNoLeidos = async (pacientesData: Paciente[], skip = 0, top = 5) => {
    if (skip === 0) {
    setLoadingEmails(true);
    } else {
      setLoadingMoreEmails(true);
    }
    
    try {
      // Obtener todos los emails de pacientes únicos
      const emailsPacientes = Array.from(new Set(
        pacientesData
          .filter(p => p.email || p.email2)
          .flatMap(p => [p.email, p.email2].filter(Boolean))
      ));
      
      if (emailsPacientes.length === 0) {
        setEmailsNoLeidos([]);
        setHasMoreEmails(false);
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/emails-no-leidos`, {
        emails: emailsPacientes,
        dias: 30, // Últimos 30 días
        historial_completo: false,
        skip: skip,
        top: top
      });
      
      const newEmails = response.data.emails || [];
      const total = response.data.total || 0;
      
      if (skip === 0) {
        // Primera carga
        setEmailsNoLeidos(newEmails);
        setEmailsSkip(top);
        setEmailsTotal(total);
        setHasMoreEmails(newEmails.length < total);
      } else {
        // Cargar más
        setEmailsNoLeidos(prev => [...prev, ...newEmails]);
        setEmailsSkip(prev => prev + top);
        // Calcular el total cargado después de agregar los nuevos emails
        const newTotalLoaded = emailsNoLeidos.length + newEmails.length;
        setHasMoreEmails(newTotalLoaded < total);
      }
      
    } catch (error) {
      console.error('Error al cargar emails no leídos:', error);
      if (skip === 0) {
      setEmailsNoLeidos([]);
      }
    } finally {
      setLoadingEmails(false);
      setLoadingMoreEmails(false);
    }
  };

  // Función para cargar más emails
  const handleCargarMasEmails = async () => {
    if (!loadingMoreEmails && hasMoreEmails) {
      await fetchEmailsNoLeidos(pacientes, emailsSkip, 5);
    }
  };

  const calcularIMC = (peso: number, altura: number) => {
    if (!peso || !altura || peso <= 0 || altura <= 0) {
      return 'No disponible';
    }
    const alturaMetros = altura / 100;
    return (peso / (alturaMetros * alturaMetros)).toFixed(1);
  };

  const getEstadoIMC = (imc: string) => {
    if (imc === 'No disponible') return { label: 'No disponible', color: 'default' };
    const imcNum = Number(imc);
    if (imcNum < 18.5) return { label: 'Bajo peso', color: 'warning' };
    if (imcNum < 25) return { label: 'Normal', color: 'success' };
    if (imcNum < 30) return { label: 'Sobrepeso', color: 'warning' };
    return { label: 'Obesidad', color: 'error' };
  };

  const getEventosPorFecha = (fecha: Date) => {
    return eventos.filter(evento => {
      const eventoFecha = new Date(evento.fecha);
      return eventoFecha.toDateString() === fecha.toDateString();
    });
  };

  const getEventosPorMes = (fecha: Date) => {
    const mes = fecha.getMonth();
    const año = fecha.getFullYear();
    return eventos.filter(evento => {
      const eventoFecha = new Date(evento.fecha);
      return eventoFecha.getMonth() === mes && eventoFecha.getFullYear() === año;
    });
  };

  // --- FUNCIONES PARA RENDERIZAR BODY Y CIDS (copiadas de EmailPreview) ---
  const extractCidsFromHtml = (html: string): string[] => {
    if (!html) return [];
    const cidRegex = /src=["']cid:([^"']+)["']/gi;
    const cids: string[] = [];
    let match;
    while ((match = cidRegex.exec(html)) !== null) {
      cids.push(match[1]);
    }
    return cids;
  };

  const replaceCidSrc = (html: string, email: any, detalle?: any): string => {
    if (!html || !email) return html;
    let newHtml = html;
    const cidsEncontrados = extractCidsFromHtml(html);
    if (cidsEncontrados.length > 0) {
      const attachments = detalle?.inline_attachments || [];
      newHtml = newHtml.replace(/src=["']cid:([^"']+)["']/gi, (match, cid) => {
        const cidNorm = cid.replace(/[<>\s]/g, '').toLowerCase();
        const att = attachments?.find((a: any) => {
          const attContentId = (a.contentId || '').replace(/[<>\s]/g, '').toLowerCase();
          const attName = (a.name || '').replace(/[<>\s]/g, '').toLowerCase();
          return attContentId === cidNorm || attName === cidNorm;
        });
        if (att) {
          const token = att.access_token;
          const backendUrl = token 
            ? `${API_BASE_URL}/email-attachment/${email.id}/${att.id}?token=${token}`
            : `${API_BASE_URL}/email-attachment/${email.id}/${att.id}`;
          return `src=\"${backendUrl}\" style=\"max-width: 100%; height: auto;\"`;
        }
        return `src=\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200' preserveAspectRatio='xMidYMid meet'%3E%3Crect width='100%25' height='100%25' fill='%23f5f5f5' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' fill='%23888' font-style='italic' font-size='14' font-family='Arial, sans-serif'%3E%3Ctspan x='50%25' dy='-0.5em'%3EImagen pegada%3C/tspan%3E%3Ctspan x='50%25' dy='1.2em'%3Eno disponible%3C/tspan%3E%3C/text%3E%3C/svg%3E\" style=\"max-width: 300px; max-height: 200px; width: auto; height: auto;\"`;
      });
    }
    return newHtml;
  };

  const processEmailBodyForDisplay = (email: any, detalle?: any): string => {
    if (!email) return '';
    const html = email.bodyHtml || email.bodyRawHtml || email.body || '';
    const sanitizedHtml = DOMPurify.sanitize(html, { ADD_ATTR: ['style'] });
    const processedHtml = replaceCidSrc(sanitizedHtml, email, detalle);
    return processedHtml;
  };
  // --- FIN FUNCIONES BODY EMAIL ---

  // Función para truncar el sujeto del email
  const truncateSubject = (subject: string, maxLength: number = 42) => {
    if (!subject) return '';
    return subject.length > maxLength ? subject.slice(0, maxLength - 3) + '...' : subject;
  };

  if (loading) {
    return (
      <Box sx={{ 
        width: '100%', 
        mt: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh'
      }}>
        {/* Logo con efecto de carga */}
        <Box sx={{ 
          position: 'relative',
          mb: 4
        }}>
          <Box
            component="img"
            src="/GPA.png"
            alt="GPA Logo"
            sx={{
              width: { xs: 100, sm: 130, md: 150 },
              height: 'auto',
              filter: 'drop-shadow(0 4px 12px rgba(25, 118, 210, 0.3))',
              animation: 'logoGlow 2s ease-in-out infinite',
              position: 'relative',
              zIndex: 2,
              '@keyframes logoGlow': {
                '0%, 100%': {
                  filter: 'drop-shadow(0 4px 12px rgba(25, 118, 210, 0.3))'
                },
                '50%': {
                  filter: 'drop-shadow(0 6px 20px rgba(25, 118, 210, 0.5))'
                }
              }
            }}
          />
          
          {/* Círculo de carga sutil alrededor del logo */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 130, sm: 160, md: 180 },
            height: { xs: 130, sm: 160, md: 180 },
            border: '2px solid rgba(25, 118, 210, 0.1)',
            borderTop: `${Math.max(2, (loadingProgress / 100) * 8)}px solid #1976d2`,
            borderRadius: '50%',
            animation: 'spin 1.5s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
              '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' }
            }
          }} />
        </Box>

        {/* Porcentaje de carga */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            letterSpacing: 0.3,
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
            color: 'rgba(25, 118, 210, 0.8)'
          }}
        >
          {Math.min(Math.round(loadingProgress), 100)}%
        </Typography>
      </Box>
    );
  }

  // Ordenar pacientes por fecha de modificación (más recientes primero)
  const pacientesOrdenados = [...pacientes].sort((a, b) => {
    const fechaA = a.ultima_modificacion ? new Date(a.ultima_modificacion).getTime() : 0;
    const fechaB = b.ultima_modificacion ? new Date(b.ultima_modificacion).getTime() : 0;
    return fechaB - fechaA;
  });

  // Tomar los 5 pacientes más recientes
  const pacientesRecientes = pacientesOrdenados.slice(0, 5);

  // Modificar obtenerProximosEventos para usar todosLosEventos
  const obtenerProximosEventos = (tipo: TipoEventoConTodos, cantidad: number = eventosAMostrar) => {
    const hoy = new Date();
    const hoyString = hoy.toLocaleDateString('en-CA'); // Usar formato local YYYY-MM-DD en lugar de UTC
    let eventosFiltrados = tipo === 'todos' ? todosLosEventos : todosLosEventos.filter(evento => evento.tipo === tipo);
    
    // Para cumpleaños, incluir los del año actual que aún no han pasado
    if (tipo === 'cumpleaños' || tipo === 'todos') {
      const cumpleaniosFuturos = eventosCumpleanios.filter(evento => {
        const fechaEvento = new Date(evento.fecha);
        const fechaHoy = new Date();
        // Si la fecha del cumpleaños ya pasó este año, no mostrarla
        return fechaEvento >= fechaHoy;
      });
      
      // Reemplazar los cumpleaños en eventosFiltrados
      if (tipo === 'cumpleaños') {
        eventosFiltrados = cumpleaniosFuturos;
      } else {
        const eventosSinCumple = eventosFiltrados.filter(e => e.tipo !== 'cumpleaños');
        eventosFiltrados = [...eventosSinCumple, ...cumpleaniosFuturos];
      }
    }
    
    const eventosHoy = eventosFiltrados.filter(evento => evento.fecha === hoyString)
      .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
    const eventosFuturos = eventosFiltrados.filter(evento => evento.fecha > hoyString)
      .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
    if (eventosHoy.length >= cantidad) {
      return eventosHoy.slice(0, cantidad);
    }
    const espaciosRestantes = cantidad - eventosHoy.length;
    const eventosFuturosLimitados = eventosFuturos.slice(0, espaciosRestantes);
    return [...eventosHoy, ...eventosFuturosLimitados];
  };

  // Función para cargar más eventos
  const handleVerMas = () => {
    setEventosAMostrar(prev => prev + 6);
  };

  // Usar la función según el filtro
  const eventosFiltrados = obtenerProximosEventos(filtroEventos);

  const getEventoIcon = (tipo: TipoEventoConTodos) => {
    switch (tipo) {
      case 'consulta': 
        return <MedicalServicesIcon sx={{ fontSize: 20 }} />;
      case 'plan alimentario': 
        return <RestaurantIcon sx={{ fontSize: 20 }} />;
      case 'recordatorio': 
        return <NotificationsIcon sx={{ fontSize: 20 }} />;
      case 'seguimiento': 
        return <MonitorWeightIcon sx={{ fontSize: 20 }} />;
      case 'cumpleaños': 
        return <CakeIcon sx={{ fontSize: 24, color: 'white' }} />;
      default: 
        return <EventIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getEventoColor = (tipo: TipoEventoConTodos) => {
    switch (tipo) {
      case 'consulta': return 'primary';
      case 'plan alimentario': return 'success';
      case 'recordatorio': return 'error';
      case 'seguimiento': return 'warning';
      case 'cumpleaños': return 'secondary';
      default: return 'default';
    }
  };

  const getEventoAvatarColor = (tipo: TipoEventoConTodos) => {
    switch (tipo) {
      case 'consulta': return '#1976d2'; // azul
      case 'plan alimentario': return '#43a047'; // verde
      case 'recordatorio': return '#e53935'; // rojo
      case 'seguimiento': return '#fbc02d'; // amarillo
      case 'cumpleaños': return '#ab47bc'; // violeta
      default: return '#757575'; // gris
    }
  };

  const formatearTipoEvento = (tipo: TipoEventoConTodos) => {
    switch (tipo) {
      case 'consulta': return 'Consulta';
      case 'plan alimentario': return 'Plan Alimentario';
      case 'recordatorio': return 'Recordatorio';
      case 'seguimiento': return 'Seguimiento';
      case 'cumpleaños': return 'Cumpleaños';
      case 'todos': return 'Todos';
      default: return tipo;
    }
  };

  const chipsEventos = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Chip
        icon={<EventIcon />}
        label="Todos"
        color={filtroEventos === 'todos' ? 'default' : 'default'}
        variant={filtroEventos === 'todos' ? 'filled' : 'outlined'}
        onClick={() => setFiltroEventos('todos' as TipoEventoConTodos)}
        size="small"
      />
      <Chip
        icon={<MedicalServicesIcon />}
        label="Consultas"
        color={filtroEventos === 'consulta' ? 'primary' : 'default'}
        variant={filtroEventos === 'consulta' ? 'filled' : 'outlined'}
        onClick={() => setFiltroEventos('consulta' as TipoEventoConTodos)}
        size="small"
      />
      <Chip
        icon={<RestaurantIcon />}
        label="Planes"
        color={filtroEventos === 'plan alimentario' ? 'success' : 'default'}
        variant={filtroEventos === 'plan alimentario' ? 'filled' : 'outlined'}
        onClick={() => setFiltroEventos('plan alimentario' as TipoEventoConTodos)}
        size="small"
      />
      <Chip
        icon={<MonitorWeightIcon />}
        label="Seguimiento"
        color={filtroEventos === 'seguimiento' ? 'warning' : 'default'}
        variant={filtroEventos === 'seguimiento' ? 'filled' : 'outlined'}
        onClick={() => setFiltroEventos('seguimiento' as TipoEventoConTodos)}
        size="small"
      />
      <Chip
        icon={<NotificationsIcon />}
        label="Recordatorios"
        color={filtroEventos === 'recordatorio' ? 'error' : 'default'}
        variant={filtroEventos === 'recordatorio' ? 'filled' : 'outlined'}
        onClick={() => setFiltroEventos('recordatorio' as TipoEventoConTodos)}
        size="small"
      />
      <Chip
        icon={<CakeIcon />}
        label="Cumpleaños"
        color={filtroEventos === 'cumpleaños' ? 'secondary' : 'default'}
        variant={filtroEventos === 'cumpleaños' ? 'filled' : 'outlined'}
        onClick={() => setFiltroEventos('cumpleaños' as TipoEventoConTodos)}
        size="small"
      />
    </Box>
  );

  const chipsGenero = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Chip
        icon={<GroupIcon />}
        label={`${pacientes.filter(p => p.genero === 'Masculino').length} Hombres`}
        color="primary"
        variant="outlined"
        onClick={() => navigate('/pacientes?genero=Masculino')}
      />
      <Chip
        icon={<GroupIcon />}
        label={`${pacientes.filter(p => p.genero === 'Femenino').length} Mujeres`}
        color="primary"
        variant="outlined"
        onClick={() => navigate('/pacientes?genero=Femenino')}
      />
      <Chip
        icon={<GroupIcon />}
        label={`${pacientes.filter(p => !p.genero || p.genero === '').length} Sin género`}
        color="default"
        variant="outlined"
        onClick={() => navigate('/pacientes?genero=SinGenero')}
      />
    </Box>
  );

  const handleOpenEmailModal = async (email: any) => {
    setSelectedEmail(email);
    setOpenEmailModal(true);
    setLoadingEmailDetalle(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/email-detalle/${email.id}`);
      setSelectedEmailDetalle(res.data);
    } catch (e) {
      setSelectedEmailDetalle(null);
    } finally {
      setLoadingEmailDetalle(false);
    }
  };

  const handleCloseEmailModal = () => {
    setOpenEmailModal(false);
    setSelectedEmail(null);
    setSelectedEmailDetalle(null);
  };

  return (
    <Box sx={{ maxWidth: { xs: '100vw', sm: 'none' }, overflowX: { xs: 'hidden', sm: 'visible' }, flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#1a202c', mb: 1 }}>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Vista general de eventos, actividad reciente y estadísticas del sistema
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Próximos Eventos */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardHeader
              title="Próximos Eventos"
              titleTypographyProps={{
                variant: 'h6',
                sx: { fontSize: '1.1rem', fontWeight: 600 }
              }}
              action={
                <IconButton 
                  onClick={() => navigate('/agenda')}
                  sx={{
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      color: '#1976d2'
                    }
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              }
            />
            <CardContent sx={{ pt: 0, pb: 2 }}>
              <Box sx={{ p: 1 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  Filtros
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {chipsEventos}
                </Box>
                {eventosFiltrados.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: 4
                  }}>
                    <EventIcon sx={{ 
                      fontSize: 48, 
                      color: 'text.secondary', 
                      mb: 2,
                      opacity: 0.6,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      No hay eventos próximos
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <List>
                      {eventosFiltrados.map((evento) => (
                        <Fragment key={evento.id}>
                          <ListItem
                            sx={{
                              transition: 'all 0.2s ease-in-out',
                              borderRadius: 1,
                              position: 'relative',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                transform: 'translateX(8px)',
                                cursor: 'pointer',
                                '& .hover-arrow': {
                                  opacity: 1,
                                  transform: 'translateX(4px)',
                                }
                              }
                            }}
                            onClick={() => {
                              localStorage.setItem('agenda_fecha_seleccionada', evento.fecha);
                              navigate('/agenda');
                            }}
                          >
                            {/* Flecha sutil en hover */}
                            <Box
                              className="hover-arrow"
                              sx={{
                                position: 'absolute',
                                right: 16,
                                top: 'calc(50% - 8px)',
                                transform: 'translateY(-50%)',
                                opacity: 0,
                                transition: 'all 0.2s ease-in-out',
                                color: 'primary.main',
                                zIndex: 1,
                              }}
                            >
                              <ArrowForwardIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <ListItemAvatar>
                              <Avatar 
                                sx={{ 
                                  bgcolor: evento.tipo === 'cumpleaños' ? '#ab47bc' : `${getEventoAvatarColor(evento.tipo)}`,
                                  color: 'white',
                                  width: 48,
                                  height: 48,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                  }
                                }}
                              >
                                {getEventoIcon(evento.tipo)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography 
                                  sx={{ 
                                    fontWeight: 500,
                                    '&:hover': {
                                      color: 'primary.main'
                                    }
                                  }}
                                >
                                  {evento.titulo}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" sx={{ color: '#2c3e50', fontWeight: 500 }}>
                                    {evento.paciente_nombre}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                    <Chip
                                      label={formatearTipoEvento(evento.tipo)}
                                      size="small"
                                      color={evento.tipo === 'cumpleaños' ? 'secondary' : getEventoColor(evento.tipo)}
                                      variant="outlined"
                                    />
                                    <Typography variant="caption" sx={{ color: '#5a6c7d', fontWeight: 500 }}>
                                      {(() => {
                                        // Crear fecha en zona horaria local para evitar problemas de UTC
                                        const [year, month, day] = evento.fecha.split('-').map(Number);
                                        const fecha = new Date(year, month - 1, day);
                                        
                                        // Para eventos, mostrar fecha y hora si está disponible
                                        const dias = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
                                        const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
                                        
                                        // Verificar si el evento tiene hora asignada
                                        // Si la fecha original es solo fecha (YYYY-MM-DD), no tiene hora
                                        const tieneHora = evento.fecha.includes('T') || evento.fecha.includes(' ');
                                        
                                        if (tieneHora) {
                                          // Mostrar fecha y hora
                                          return `${dias[fecha.getDay()]}, ${fecha.getDate()} ${meses[fecha.getMonth()]} ${fecha.toLocaleTimeString('es-ES', { 
                                            hour: '2-digit', 
                                            minute: '2-digit',
                                            hour12: false 
                                          })}`;
                                        } else {
                                          // Solo mostrar fecha
                                          return `${dias[fecha.getDay()]}, ${fecha.getDate()} ${meses[fecha.getMonth()]}`;
                                        }
                                      })()}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                          <Divider variant="inset" component="li" />
                        </Fragment>
                      ))}
                    </List>
                    {/* Botón "Ver más" */}
                    {eventosFiltrados.length === eventosAMostrar && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={handleVerMas}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 3,
                            py: 1,
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#1565c0',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Ver más eventos
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Emails No Leídos */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardHeader
              title="Actividad Reciente"
              titleTypographyProps={{
                variant: 'h6',
                sx: { fontSize: '1.1rem', fontWeight: 600 }
              }}
              action={
                <IconButton 
                  onClick={() => navigate('/email')}
                  sx={{
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      color: '#1976d2'
                    }
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              }
            />
            <CardContent sx={{ pt: 0, pb: 2 }}>
              <Box sx={{ p: 1 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  Últimos 30 días
                </Typography>
                {loadingEmails ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: 4
                  }}>
                    <LinearProgress sx={{ width: '66.67%', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Cargando emails...
                    </Typography>
                  </Box>
                ) : emailsNoLeidos.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: 4
                  }}>
                    <EmailIcon sx={{ 
                      fontSize: 48, 
                      color: 'text.secondary', 
                      mb: 2,
                      opacity: 0.6,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      No hay emails no leídos
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <List>
                      {emailsNoLeidos.map((email) => (
                        <Fragment key={email.id}>
                          <ListItem
                            sx={{
                              transition: 'all 0.2s ease-in-out',
                              borderRadius: 1,
                              position: 'relative',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                transform: 'translateX(8px)',
                                cursor: 'pointer',
                                '& .hover-eye': {
                                  opacity: 1,
                                  transform: 'translateX(4px)',
                                }
                              }
                            }}
                            onClick={() => handleOpenEmailModal(email)}
                          >
                            {/* Ojo sutil en hover, en la misma posición que la flecha */}
                            <Box
                              className="hover-eye"
                              sx={{
                                position: 'absolute',
                                right: 16,
                                top: 'calc(50% - 12px)',
                                transform: 'translateY(-50%)',
                                opacity: 0.5,
                                transition: 'all 0.2s ease-in-out',
                                color: 'primary.main',
                                zIndex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 24
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 24 }} />
                            </Box>
                            <ListItemAvatar>
                              <Avatar 
                                sx={{ 
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  width: 48,
                                  height: 48,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                  }
                                }}
                              >
                                <EmailIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography 
                                    sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    {truncateSubject(email.subject || 'Sin asunto', 42)}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" sx={{ color: '#2c3e50', fontWeight: 500 }}>
                                    {email.from.name || email.from.email}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                    {(() => {
                                      const chips = [];
                                      
                                      // Chip para emails no leídos
                                      if (!email.isRead) {
                                        chips.push(
                                    <Chip
                                            key="unread"
                                      label="No leído"
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                            icon={<MarkEmailUnreadIcon sx={{ fontSize: 16 }} />}
                                          />
                                        );
                                      }
                                      
                                      // Chip para emails importantes
                                      if (email.isImportant || email.importance === 'high') {
                                        chips.push(
                                          <Chip
                                            key="important"
                                            label="Importante"
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            icon={<PriorityHighIcon sx={{ fontSize: 16 }} />}
                                          />
                                        );
                                      }
                                      
                                      // Chip para emails marcados
                                      if (email.isFlagged) {
                                        chips.push(
                                          <Chip
                                            key="flagged"
                                            label="Marcado"
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                            icon={<FlagIcon sx={{ fontSize: 16 }} />}
                                          />
                                        );
                                      }
                                      
                                      // Chip para emails con adjuntos
                                      if (email.hasAttachments) {
                                        chips.push(
                                          <Chip
                                            key="attachments"
                                            label="Adjuntos"
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            icon={<AttachmentIcon sx={{ fontSize: 16 }} />}
                                          />
                                        );
                                      }
                                      
                                      // Si no hay chips específicos, mostrar estado general
                                      if (chips.length === 0) {
                                        chips.push(
                                          <Chip
                                            key="read"
                                            label="Leído"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                          />
                                        );
                                      }
                                      
                                      return chips;
                                    })()}
                                    <Typography variant="caption" sx={{ color: '#5a6c7d', fontWeight: 500 }}>
                                      {(() => {
                                        const fecha = new Date(email.receivedDateTime);
                                        const ahora = new Date();
                                        
                                        // Crear fechas para comparar solo la fecha (sin hora)
                                        const fechaSolo = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
                                        const ahoraSolo = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
                                        
                                        // Calcular diferencia en días
                                        const diffMs = ahoraSolo.getTime() - fechaSolo.getTime();
                                        const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                                        
                                        if (diffDias === 0) {
                                          // Es hoy
                                          const diffMin = Math.floor((ahora.getTime() - fecha.getTime()) / (1000 * 60));
                                          if (diffMin < 60) {
                                            return `Hace ${diffMin} min`;
                                          }
                                          const diffHoras = Math.floor(diffMin / 60);
                                          if (diffHoras < 24) {
                                            return `Hace ${diffHoras} h`;
                                          }
                                          // Si por alguna razón pasa de 24h pero sigue siendo hoy, mostrar la hora
                                          return fecha.toLocaleTimeString('es-ES', { 
                                            hour: '2-digit', 
                                            minute: '2-digit',
                                            hour12: false 
                                          });
                                        } else if (diffDias === 1) {
                                          return 'Ayer';
                                        } else {
                                          const dias = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
                                          const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
                                          return `${dias[fecha.getDay()]}, ${fecha.getDate()} ${meses[fecha.getMonth()]}`;
                                        }
                                      })()}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                          <Divider variant="inset" component="li" />
                        </Fragment>
                      ))}
                    </List>
                    {/* Botón "Ver más" si hay más de 5 emails */}
                    {emailsNoLeidos.length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        {hasMoreEmails && !loadingMoreEmails ? (
                          <Button
                            variant="contained"
                            onClick={handleCargarMasEmails}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              px: 3,
                              py: 1,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: '#1565c0',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Cargar más
                          </Button>
                        ) : hasMoreEmails && loadingMoreEmails ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={24} sx={{ color: '#1976d2' }} />
                          </Box>
                        ) : !hasMoreEmails ? (
                          <Button
                            variant="contained"
                            onClick={() => navigate('/email')}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              px: 3,
                              py: 1,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: '#1565c0',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Ver todos los emails
                          </Button>
                        ) : null}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Resumen de Pacientes */}
        <Grid item xs={12} md={12} lg={4}>
          <Card>
            <CardHeader
              title="Pacientes"
              titleTypographyProps={{
                variant: 'h6',
                sx: { fontSize: '1.1rem', fontWeight: 600 }
              }}
              action={
                <IconButton 
                  onClick={() => navigate('/pacientes')}
                  sx={{
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      color: '#1976d2'
                    }
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              }
            />
            <CardContent sx={{ pt: 1, pb: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 0, pl: 1 }}>
                  <Typography variant="h6" component="h2" sx={{ mr: 2 }}>
                    {pacientes.length} Pacientes
                  </Typography>
                  {chipsGenero}
                </Box>
                <Box sx={{ p: 1, pl: 2 }}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    Últimos Modificados
                  </Typography>
                  <List>
                    {pacientesRecientes.map((paciente) => (
                      <Fragment key={paciente.id}>
                        <ListItem
                          sx={{
                            transition: 'all 0.2s ease-in-out',
                            borderRadius: 1,
                            position: 'relative',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.08)',
                              transform: 'translateX(8px)',
                              cursor: 'pointer',
                              '& .hover-arrow': {
                                opacity: 1,
                                transform: 'translateX(4px)',
                              }
                            }
                          }}
                          onClick={() => navigate(`/pacientes/${paciente.id}`)}
                        >
                          {/* Flecha sutil en hover */}
                          <Box
                            className="hover-arrow"
                            sx={{
                              position: 'absolute',
                              right: 16,
                              top: 'calc(50% - 8px)',
                              transform: 'translateY(-50%)',
                              opacity: 0,
                              transition: 'all 0.2s ease-in-out',
                              color: 'primary.main',
                              zIndex: 1,
                            }}
                          >
                            <ArrowForwardIcon sx={{ fontSize: 16 }} />
                          </Box>
                          <ListItemAvatar>
                            <Avatar 
                              src={getFileUrl(paciente?.ruta_imagen_perfil)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8
                                }
                              }}
                            >
                              {paciente.nombre.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography 
                                sx={{ 
                                  fontWeight: 500,
                                  '&:hover': {
                                    color: 'primary.main'
                                  }
                                }}
                              >
                                {paciente.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" sx={{ color: '#2c3e50', fontWeight: 500 }}>
                                  {paciente.email}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                  {paciente.obra_social && (
                                    <Chip label={paciente.obra_social} size="small" />
                                  )}
                                  {paciente.genero && (
                                    <Chip label={paciente.genero} size="small" />
                                  )}
                                  {typeof paciente.edad === 'number' && !isNaN(paciente.edad) && paciente.edad > 0 && (
                                    <Chip label={`${paciente.edad} años`} size="small" />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                        <Divider variant="inset" component="li" />
                      </Fragment>
                    ))}
                  </List>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={openEmailModal}
        onClose={handleCloseEmailModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 0, minHeight: 320 } }}
      >
        <DialogTitle 
          sx={{ 
            fontWeight: 600, 
            fontSize: 20, 
            pb: 1, 
            pr: 5, 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            minHeight: 56
          }}
        >
          {selectedEmail?.subject || 'Sin asunto'}
          <IconButton
            onClick={handleCloseEmailModal}
            sx={{ position: 'absolute', right: 16, top: 16, color: 'primary.contrastText' }}
            size="small"
          >
            <span style={{ fontSize: 22 }}>&times;</span>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.paper', p: 3, minHeight: 220 }}>
          {loadingEmailDetalle ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
              <CircularProgress size={32} />
            </Box>
          ) : selectedEmail && selectedEmailDetalle ? (
            <>
              {selectedEmailDetalle.bodyHtml || selectedEmailDetalle.bodyRawHtml || selectedEmailDetalle.body ? (
                <Box sx={{ mb: 2 }}>
                  <div
                    dangerouslySetInnerHTML={{ __html: processEmailBodyForDisplay(selectedEmailDetalle, selectedEmailDetalle) }}
                    style={{ fontFamily: 'inherit', lineHeight: 1.6, wordWrap: 'break-word' }}
                    className="email-content"
                  />
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">Sin contenido</Typography>
              )}
              {/* Adjuntos */}
              {(selectedEmailDetalle.attachments?.length > 0 || selectedEmailDetalle.inline_attachments?.length > 0) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Adjuntos</Typography>
                  <List dense>
                    {selectedEmailDetalle.attachments?.map((attachment: any) => (
                      <ListItem key={attachment.id} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <AttachmentIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={attachment.name}
                          secondary={`${(attachment.size / 1024).toFixed(1)} KB`}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const token = attachment.access_token;
                            const url = token
                              ? `${API_BASE_URL}/email-attachment/${selectedEmail.id}/${attachment.id}?token=${token}`
                              : `${API_BASE_URL}/email-attachment/${selectedEmail.id}/${attachment.id}`;
                            window.open(url, '_blank');
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </ListItem>
                    ))}
                    {selectedEmailDetalle.inline_attachments?.map((attachment: any) => (
                      <ListItem key={attachment.id} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <VisibilityIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${attachment.name} (inline)`}
                          secondary={`${(attachment.size / 1024).toFixed(1)} KB`}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const token = attachment.access_token;
                            const url = token
                              ? `${API_BASE_URL}/email-attachment/${selectedEmail.id}/${attachment.id}?token=${token}`
                              : `${API_BASE_URL}/email-attachment/${selectedEmail.id}/${attachment.id}`;
                            window.open(url, '_blank');
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          <Button onClick={handleCloseEmailModal} variant="outlined">Cerrar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (selectedEmail) {
                const paciente = pacientes.find(p => 
                  p.email === selectedEmail.from.email || p.email2 === selectedEmail.from.email
                );
                if (paciente) {
                  localStorage.setItem('selectedPacienteForEmail', JSON.stringify(paciente));
                  navigate('/email');
                }
              }
            }}
            sx={{ ml: 2 }}
          >
            Ir a emails del paciente
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard; 